import type { AutoscaleRule, VolumeSummary } from '../../hooks/useAutoscaleRulesQuery';

type VolumeData = NonNullable<AutoscaleRule['volumeData']>;

function emptyVolumeData(): VolumeData {
  return { datafiable: [], potentiallyDatafiable: [], excluded: [] };
}

/**
 * Derive the rule-level aggregates from the categorized volume buckets, mirroring
 * `transformApiRuleToFrontend` so an optimistic patch stays consistent with what a
 * real refetch would produce.
 */
function recomputeAggregates(
  volumeData: VolumeData
): Pick<AutoscaleRule, 'volumes' | 'capacity' | 'utilization' | 'exceptions'> {
  const datafiable = volumeData.datafiable;
  const volumes = datafiable.length;
  const capacity = datafiable.reduce((sum, v) => sum + (v.diskSize ?? 0), 0);
  const averageUtilization = volumes
    ? datafiable.reduce((sum, v) => sum + (v.utilization ?? 0), 0) / volumes
    : 0;
  const utilization =
    volumes === 0 && averageUtilization === 0
      ? 'N/A'
      : averageUtilization
        ? `${Math.round(averageUtilization)}%`
        : '0%';

  return {
    volumes,
    capacity,
    utilization,
    exceptions: volumeData.excluded.length,
  };
}

function findAndRemove(
  buckets: VolumeSummary[],
  volumeId: string
): { found: VolumeSummary | null; rest: VolumeSummary[] } {
  let found: VolumeSummary | null = null;
  const rest = buckets.filter((v) => {
    if (!found && v.volumeId === volumeId) {
      found = v;
      return false;
    }
    return true;
  });
  return { found, rest };
}

/**
 * Move a volume into the `excluded` bucket (creating an exception). The volume is
 * located in `datafiable` first, then `potentiallyDatafiable`. Aggregates are
 * recomputed from the resulting buckets.
 */
export function applyCreateException(rule: AutoscaleRule, volumeId: string): AutoscaleRule {
  const current = rule.volumeData ?? emptyVolumeData();

  const fromDatafiable = findAndRemove(current.datafiable, volumeId);
  const fromPotential = fromDatafiable.found
    ? { found: null as VolumeSummary | null, rest: current.potentiallyDatafiable }
    : findAndRemove(current.potentiallyDatafiable, volumeId);

  const moved = fromDatafiable.found ?? fromPotential.found;
  if (!moved) {
    return rule;
  }

  const nextVolumeData: VolumeData = {
    datafiable: fromDatafiable.rest,
    potentiallyDatafiable: fromPotential.rest,
    excluded: [...current.excluded, moved],
  };

  return {
    ...rule,
    ...recomputeAggregates(nextVolumeData),
    volumeData: nextVolumeData,
  };
}

/**
 * Remove a volume from the `excluded` bucket (clearing an exception). We only drop
 * it from `excluded` here; the correct destination bucket (matching vs potential)
 * is filled in by the background refetch.
 */
export function applyRemoveException(rule: AutoscaleRule, volumeId: string): AutoscaleRule {
  const current = rule.volumeData ?? emptyVolumeData();
  const { found, rest } = findAndRemove(current.excluded, volumeId);
  if (!found) {
    return rule;
  }

  const nextVolumeData: VolumeData = {
    datafiable: current.datafiable,
    potentiallyDatafiable: current.potentiallyDatafiable,
    excluded: rest,
  };

  return {
    ...rule,
    ...recomputeAggregates(nextVolumeData),
    volumeData: nextVolumeData,
  };
}
